import { useMemo } from "react";

import { useDataProvider } from "@refinedev/core";

import { FIVE_MINUTE_STALE_TIME } from "@/providers/data/fhir-data-provider";
import { PRACTITIONER_ROLE_CHUNK_SIZE } from "@/utils/fhir-constants";
import { FhirResourceType, chunk, deduplicateById, formatters } from "@/utils/fhir-utils";

import { fhirR4 } from "@smile-cdr/fhirts";
import { useQuery } from "@tanstack/react-query";

export interface UseAllPractitionerRolesResult {
  allPractitionerRoles: fhirR4.PractitionerRole[];
  organizations: fhirR4.Organization[];
  isLoading: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * Fetches ALL PractitionerRoles across all organizations for the given practitioners.
 * Uses _include=PractitionerRole:organization to fetch Organizations in the same query.
 * Chunks practitioner references to avoid URL length limits (chunk size: 50).
 * Used by the practitioner table to show all clinics a practitioner belongs to.
 */
export const useAllPractitionerRoles = (
  practitionerResources: fhirR4.Practitioner[]
): UseAllPractitionerRolesResult => {
  const dataProvider = useDataProvider();

  const practitionerRefs = useMemo(
    () =>
      practitionerResources
        .map(p => p.id)
        .filter((id): id is string => !!id)
        .map(id => formatters.formatPractitioner(id))
        .sort(),
    [practitionerResources]
  );

  const queryKey = useMemo(
    () => [FhirResourceType.PractitionerRole, "all-by-practitioner", practitionerRefs.join("|")],
    [practitionerRefs]
  );

  const query = useQuery({
    queryKey,
    enabled: practitionerRefs.length > 0,
    staleTime: FIVE_MINUTE_STALE_TIME,
    queryFn: async () => {
      // Chunk practitioner references to avoid URL length limits
      // Use _include to fetch Organizations in the same query
      const chunks = chunk(practitionerRefs, PRACTITIONER_ROLE_CHUNK_SIZE);
      const results = await Promise.all(
        chunks.map(async refsChunk => {
          try {
            const { data } = await dataProvider().getList<fhirR4.PractitionerRole>({
              resource: FhirResourceType.PractitionerRole,
              filters: [{ field: "practitioner", operator: "eq", value: refsChunk.join(",") }],
              meta: {
                fetchAllPages: true,
                customQueryParameters: ["_include=PractitionerRole:organization"],
              },
            });

            // Parse response: data contains both PractitionerRoles and Organizations
            const allResources = (data as fhirR4.Resource[]) ?? [];
            const roles: fhirR4.PractitionerRole[] = [];
            const orgs: fhirR4.Organization[] = [];

            for (const resource of allResources) {
              if (!resource) {
                continue;
              }
              if (resource.resourceType === FhirResourceType.PractitionerRole) {
                roles.push(resource as fhirR4.PractitionerRole);
              } else if (resource.resourceType === FhirResourceType.Organization) {
                orgs.push(resource as fhirR4.Organization);
              }
            }

            return { roles, organizations: orgs };
          } catch {
            return { roles: [], organizations: [] };
          }
        })
      );

      // Deduplicate both roles and organizations across chunks
      const allRoles = results.flatMap(r => r.roles);
      const allOrgs = results.flatMap(r => r.organizations);

      return {
        roles: deduplicateById(allRoles),
        organizations: deduplicateById(allOrgs),
      };
    },
  });

  return {
    allPractitionerRoles: query.data?.roles ?? [],
    organizations: query.data?.organizations ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};
